import { george, helpers, i18n, NetbankingAPI, pr, theme } from "aaa";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { IconMoney, LightSeparatorLine } from "components/util/primitives";
import { ui } from "components/util/uiHelpers";
import * as FileSaver from "file-saver";
import { FormattedHTMLMessage } from "i18n/util";
import { observer } from "mobx-react";
import { NetbankingAPIClient } from "network/APIClient";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { getDefaultKeyPressHandler } from "util/helpers";
import { BSPOptionsRoutes } from "./BSPOptionsRoutes";
import { BSPOptionsStore } from "./BSPOptionsStore";
import { BSPOptionsInfoBox } from "./components/BSPOptionsInfoBox";
import { BSPOptionsPayoutSummarySection } from "./components/BSPOptionsPayoutSummarySection";
import { DownloadButton } from "./components/DownloadButton";



type IProps = RouteComponentProps<{ id: string }> & i18n.InjectedIntlProps & {
    store: BSPOptionsStore;
};

export const BSPOptionsPayoutSummary = observer(({ store }: IProps) => {
    const [didAutofocus, setDidAutofocus] = React.useState(false);
    const ref = React.useRef<HTMLAnchorElement | null>(null);

    React.useEffect(() => {
        if (ref.current && !didAutofocus) {
            ref.current.focus();
            setDidAutofocus(true);
        }
    }, []);

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;

    const handleClickSign = async () => {
        try {
            await store.postPayout();
            pushRoute(BSPOptionsRoutes.SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    const handleDownload = async () => {
        try {
            if (!store.buildingsAccount || !store.accountsStore?.selectedAccount) {
                return;
            }

            try {
                const target = NetbankingAPI.postBuildingsPayoutPdf(store.buildingsAccount.id, store.accountsStore.selectedAccount.id);
                const response = await NetbankingAPIClient.request(target);

                if (helpers.isAndroid() || helpers.isIos()) {
                    const arrayBuffer = await response.arrayBuffer();
                    store.pdfData = new Uint8Array(arrayBuffer);
                    pushRoute(BSPOptionsRoutes.PDF);
                } else {
                    const contentDispositionHeader = response.headers.get("content-disposition");
                    const contentDispositionParts = contentDispositionHeader?.split(";").map(part => part.trim());
                    const filenamePart = contentDispositionParts?.find(part => part.startsWith("filename")) ?? "";
                    const rawFilename = filenamePart.split("=")[1] ?? "";
                    const filename = rawFilename.replace(/["']/g, "").replace(/[<>:"/\\|?*\x00-\x1F]/g, "").trim();

                    const blob = await response.blob();

                    FileSaver.saveAs(blob, filename);
                }
            } catch (error) {
                pushRoute("/error");
            }
        } catch (error) {
            pushRoute("/error");
        }
    };

    const handleBack = async () => {
        pushRoute(BSPOptionsRoutes.ROOT);
    };

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;

    return (
        <BackgroundComponent style={{ paddingTop: 24, paddingBottom: theme.MARGIN }}>
            <div style={pr.STYLE_CENTERED_CARD}>
                <a
                    ref={ref}
                    style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexGrow: 1, flexBasis: 0 }}
                    role="button"
                    tabIndex={0}
                    onClick={handleBack}
                    onKeyPress={getDefaultKeyPressHandler(handleBack)}
                    aria-label={ui.__("common.oneStepBack")}
                >
                    <pr.IconChevronLeft className="theme-secondary-action-label-color" style={{ display: "block", flexShrink: 0 }} />
                    {!isMobile && (
                        <pr.P_16_CONTENT className="theme-secondary-action-label-color" style={{ fontWeight: 600 }}>
                            {ui.__("common.oneStepBack")}
                        </pr.P_16_CONTENT>
                    )}
                </a>
            </div>
            <george.Card
                disableTopSpacing
                style={{
                    marginTop: 16,
                    borderRadius: 8,
                    boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)"
                }}
            >
                <div style={{ textAlign: "center", padding: 32 }}>
                    <IconMoney style={{ width: 32, height: 32, color: "#027455", padding: 16, borderRadius: 32, backgroundColor: "#F2F9F7" }} />
                    <pr.P_22_TITLE style={{ fontWeight: 700, lineHeight: "28px", color: ui.isDarkMode ? "white" : "#0A285C", marginTop: 8 }}>
                        {store.selectedPayout === "payout" ? ui.__("bspOptions.summary.payout.headline") : ui.__("bspOptions.summary.payoutLegalGuardian.headline")}
                    </pr.P_22_TITLE>
                </div>
                <LightSeparatorLine />
                <div style={{ padding: 32 }}>
                    <BSPOptionsPayoutSummarySection isAccountSelectable store={store} />
                </div>
                <LightSeparatorLine />
                <div style={{ padding: 32 }}>
                    <pr.P_18_TITLE style={{ fontWeight: 700, lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                        {ui.__("bspOptions.summary.payout.documents.title")}
                    </pr.P_18_TITLE>
                    <pr.P_16_CONTENT style={{ lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C", marginTop: 4 }}>
                        {ui.__("bspOptions.summary.payout.documents.text")}
                    </pr.P_16_CONTENT>
                    <DownloadButton onClick={handleDownload} style={{ marginTop: 16 }}>
                        {ui.__("bspOptions.summary.payout.documents.download.button")}
                    </DownloadButton>
                    <pr.P_16_CONTENT style={{ lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C", marginTop: 16 }}>
                        {ui.__("bspOptions.summary.payout.documents.additionalText")}
                    </pr.P_16_CONTENT>
                    <BSPOptionsInfoBox style={{ width: "100%", marginTop: 16 }}>
                        <pr.P_14_TITLE style={{ fontWeight: 700, lineHeight: "21px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                            {ui.__("bspOptions.summary.payout.infoBox.title")}
                        </pr.P_14_TITLE>
                        <pr.P_14_CONTENT style={{ lineHeight: "21px", marginTop: 16 }}>
                            <FormattedHTMLMessage id="bspOptions.summary.payout.infoBox.text" />
                        </pr.P_14_CONTENT>
                    </BSPOptionsInfoBox>
                </div>
                <LightSeparatorLine />
                <div style={{ textAlign: "center", padding: 32 }}>
                    <GdsFakeActionButton color="primary" onClick={handleClickSign} fullWidth={isMobile}>
                        {ui.__("signing.button.start.signing")}
                    </GdsFakeActionButton>
                </div>
            </george.Card>
        </BackgroundComponent>
    );
});



// WEBPACK FOOTER //
// ./src/components/BSPOptions/BSPOptionsPayoutSummary.tsx